import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { RoutePrefetchLink } from "@/components/ui/route-prefetch-link";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { Github } from "lucide-react";
import { GitHubOrgSelector } from "../GitHubOrgSelector";
import { BlacksmithLogomarkSVG, BlacksmithWordmarkSVG } from "../BlacksmithLogoSVG";
import { CodesmithLottieIcon } from "@/components/icons/CodesmithLottieIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useHasHoverPointer, useIsMobile } from "@/hooks/use-mobile";
import { isTypingTarget } from "@/lib/isTypingTarget";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { cn } from "@/lib/utils";
import { AppHeaderBar } from "@/components/ui/app-header";
import { CollapsedIconTooltip, RAIL_REDESIGN_CLASS, RAIL_ROW, RAIL_ROW_ACTIVE, RailRowContents, SidebarAdminRow, SidebarGlobalFooter } from "./SidebarGlobalChrome";
import { useCodesmithSidebarVariant } from "@/components/codesmith/useCachedSidebarRedesignEnabled";
import { SidebarBlockedOrgBanner, useSidebarGlobalBehaviors } from "./useSidebarGlobalBehaviors";
import { useActiveApp, isStandaloneAccountPath, isAdminPath, settingsPagePath } from "./useActiveApp";
import { getCachedLastActiveOrg, useCodesmithEnabledWithCache } from "./useCodesmithEnabledCache";
import { RAIL_WIDTH, RAIL_REDESIGN_WIDTH, RAIL_EXPANDED_WIDTH, RAIL_WIDTH_CSS_VAR, RAIL_WIDTH_TRANSITION_MS, RAIL_TRANSITION_EASING } from "./railConstants";

/**
 * Slim app-switcher rail pinned to the left edge of the app. Hosts the
 * Blacksmith logo, top-level app entries (GitHub Actions, Codesmith), and the
 * global footer controls (support, settings, notifications, org switcher).
 *
 * The rail is 48px wide by default; clicking the logo pins it open at 200px
 * (row labels, org selector, full wordmark) until the logo is clicked again —
 * it does not auto-collapse on hover-out or navigation. The expanded rail is
 * part of the page layout: the contextual sidebar and main content slide
 * over in lockstep with the panel's own width transition (see `layoutWidth`
 * below and the matching `left` transition on the contextual Sidebar), rather
 * than being covered by an overlay.
 */

// Master switch for the rail's whole-panel hover expansion. Off by default:
// the rail stays 48px and each row surfaces its own tooltip, while the
// vCPU/status widget expands as a standalone hover overlay (RailStatusHoverCard)
// rather than growing the whole rail. Flip to true to restore the original
// behavior where hovering expands the entire rail to reveal row labels. Typed
// as boolean so both code paths stay type-checked at the default value.
const RAIL_HOVER_EXPAND: boolean = false;

// Hover-intent dwell before expanding. The pointer must rest on the rail for
// this long before the wipe starts, so a quick pass-over (e.g. on the way to
// the main content) never kicks off a half-open-then-reverse animation. Close
// stays instant, so the rail still feels snappy.
const RAIL_OPEN_DELAY_MS = 200;

// The mutually-exclusive rail rows that can read as active.
type RailSection = "actions" | "codesmith" | "usage" | "admin";

// Slide/wipe transition for the expand/collapse. easeOutQuint (see
// RAIL_TRANSITION_EASING): the panel starts moving immediately and
// decelerates smoothly into its final width (an ease-out reads far smoother
// here than an ease-in-out, which hesitates then snaps). Duration/easing are
// shared with the contextual Sidebar's `left` transition (railConstants.ts)
// so the panel, the pushed main content, and the fixed-position contextual
// sidebar all slide in lockstep rather than one visibly lagging the others.
//
// This is an inline style, not a Tailwind class: Tailwind's content scanner
// only recognizes class strings that appear literally in source, and this
// value is assembled from imported constants, so a `[transition:...]`
// arbitrary-value class built from it would never get a matching CSS rule
// generated — the rail would silently skip the transition and jump straight
// to its new width. That's invisible on pages with a contextual Sidebar
// (its own `left` transition carries the visible slide), but on Codesmith
// pages, which push a plain flex sibling (SessionSidebar) with nothing else
// animating, it read as no transition at all.
const RAIL_TRANSITION_STYLE = `width ${RAIL_WIDTH_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING}`;

// Set on navigation; hover expansion is ignored while true so the panel does
// not pop back open over the section the user just clicked into. Cleared once
// the pointer leaves the rail (a fresh hover re-enables expansion). Lives at
// module scope because the rail can still unmount and remount within a session
// (its Suspense boundary in SidebarLayout) and the flag must survive that
// remount.
let suppressExpandUntilPointerLeaves = false;

// Clears the post-navigation suppression once the pointer is actually off the
// rail. React's onMouseLeave can't be relied on for this: clicking a row
// collapses the panel (200→48px) instantly under a stationary cursor, and a
// resize out from under the pointer doesn't dispatch mouseleave — so the flag
// would otherwise stay armed and swallow the next genuine hover. A
// document-level mousemove checks the live pointer position instead.
//
// The listener lives at module scope, like the flag it clears: it must stay
// attached across any rail remount, because a mousemove missed in an
// instance-bound listener's teardown/re-arm gap could let the
// pointer travel off the rail unseen and then re-enter with the first
// delivered event already targeting the panel — leaving the flag stuck and
// hover dead until the pointer exits and re-enters. The rail is resolved from
// the DOM at event time (live rail or its suspense skeleton), so the check
// works whichever instance is mounted. If the rail leaves the page entirely,
// the first mousemove finds no rail, clears the flag, and removes the
// listener, so nothing lingers.
let suppressClearListener: ((ev: MouseEvent) => void) | null = null;
function removeSuppressClearListener() {
  if (suppressClearListener) {
    document.removeEventListener("mousemove", suppressClearListener);
    suppressClearListener = null;
  }
}

// Persists the logo-click pin (see `expanded`) across that same remount.
// Read once as the initial state on mount and written back on every change,
// so when the rail comes back after unmounting it resumes exactly how it was
// left, rather than resetting to collapsed each time.
let pinnedExpanded = false;
function armSuppressUntilPointerLeaves() {
  suppressExpandUntilPointerLeaves = true;
  removeSuppressClearListener();
  const check = (ev: MouseEvent) => {
    const target = ev.target as HTMLElement | null;
    if (target?.closest('[data-slot="app-switcher-rail"], [data-slot="app-switcher-rail-skeleton"]')) {
      return;
    }
    suppressExpandUntilPointerLeaves = false;
    removeSuppressClearListener();
  };
  suppressClearListener = check;
  document.addEventListener("mousemove", check);
}
export default function AppSwitcherRail() {
  const $ = _c(139);
  const router = useRouter();
  const activeApp = useActiveApp();
  const routeSection = isAdminPath(router.pathname) ? "admin" : router.pathname === "/[org]/usage" || router.pathname === "/usage" ? "usage" : isStandaloneAccountPath(router.pathname) ? null : activeApp === "codesmith" ? "codesmith" : "actions";
  const [optimisticSection, setOptimisticSection] = useState(null);
  const activeSection = optimisticSection ?? routeSection;
  const railRowNavigationRef = useRef(false);
  useIsMobile();
  const hasHoverPointer = useHasHoverPointer();
  const {
    activeGithubOrg,
    codesmithAutomationsEnabled,
    orgsResolved
  } = useActiveGithubOrgForUserNonSuspense();
  const codesmithEnabled = useCodesmithEnabledWithCache() !== false;
  const urlOrg = router.query.org;
  let t0;
  if ($[0] !== activeGithubOrg || $[1] !== orgsResolved || $[2] !== urlOrg) {
    t0 = (typeof urlOrg === "string" ? urlOrg : undefined) || activeGithubOrg || (orgsResolved ? undefined : getCachedLastActiveOrg());
    $[0] = activeGithubOrg;
    $[1] = orgsResolved;
    $[2] = urlOrg;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const currentOrg = t0;
  const runsJobsHref = currentOrg ? `/${currentOrg}/runs/jobs` : "/runs/jobs";
  const sidebarVariant = useCodesmithSidebarVariant(currentOrg ?? null);
  const redesign = sidebarVariant === "redesign";
  const sessionsNewHref = currentOrg ? `/${currentOrg}/sessions/new` : "/sessions/new";
  const {
    activeGithubOrgBlocked: globalOrgBlocked,
    gPrefixActiveRef
  } = useSidebarGlobalBehaviors();
  const [expanded, setExpanded] = useState(pinnedExpanded);
  const slim = !expanded;
  const layoutWidth = expanded ? RAIL_EXPANDED_WIDTH : redesign ? RAIL_REDESIGN_WIDTH : RAIL_WIDTH;
  const [instantClose, setInstantClose] = useState(false);
  const [codesmithRowHovered, setCodesmithRowHovered] = useState(false);
  const panelRef = useRef(null);
  const openTimerRef = useRef(null);
  const portalMouseListenerRef = useRef(null);
  const portalObserverRef = useRef(null);
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const clearOpenTimer = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (portalMouseListenerRef.current) {
        document.removeEventListener("mousemove", portalMouseListenerRef.current);
        portalMouseListenerRef.current = null;
      }
      if (portalObserverRef.current) {
        portalObserverRef.current.disconnect();
        portalObserverRef.current = null;
      }
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const removePortalTracker = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      removePortalTracker();
      const checkMouse = ev => {
        const target = ev.target as HTMLElement | null;
        if (!target) {
          return;
        }
        const overPanel = panelRef.current?.contains(target);
        const overPortal = target.closest("[data-radix-popper-content-wrapper]");
        const portalOpen = document.querySelector("[data-radix-popper-content-wrapper]");
        if (!overPanel && !overPortal && !portalOpen) {
          setExpanded(false);
          removePortalTracker();
        }
      };
      portalMouseListenerRef.current = checkMouse;
      document.addEventListener("mousemove", checkMouse);
      const observer = new MutationObserver(() => {
        if (document.querySelector("[data-radix-popper-content-wrapper]")) {
          return;
        }
        if (!panelRef.current?.matches(":hover")) {
          setExpanded(false);
        }
        removePortalTracker();
      });
      observer.observe(document.body, {
        childList: true
      });
      portalObserverRef.current = observer;
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const installPortalTracker = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = section => e => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      setOptimisticSection(section);
      railRowNavigationRef.current = true;
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleRailRowClick = t4;
  let t5;
  if ($[8] !== expanded || $[9] !== hasHoverPointer) {
    t5 = () => {
      if (!RAIL_HOVER_EXPAND) {
        return;
      }
      if (!hasHoverPointer || suppressExpandUntilPointerLeaves) {
        return;
      }
      removePortalTracker();
      if (expanded) {
        return;
      }
      clearOpenTimer();
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null;
        if (!hasHoverPointer || suppressExpandUntilPointerLeaves) {
          return;
        }
        if (!panelRef.current?.matches(":hover")) {
          return;
        }
        setExpanded(true);
      }, RAIL_OPEN_DELAY_MS);
    };
    $[8] = expanded;
    $[9] = hasHoverPointer;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleMouseEnter = t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_0 => {
      if (!RAIL_HOVER_EXPAND) {
        return;
      }
      clearOpenTimer();
      suppressExpandUntilPointerLeaves = false;
      removeSuppressClearListener();
      const related = e_0.relatedTarget;
      if (related instanceof HTMLElement && related.closest("[data-radix-popper-content-wrapper]")) {
        installPortalTracker();
        return;
      }
      if (document.querySelector("[data-radix-popper-content-wrapper]")) {
        installPortalTracker();
        return;
      }
      setExpanded(false);
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleMouseLeave = t6;
  let t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      removePortalTracker();
      clearOpenTimer();
    };
    t8 = [removePortalTracker, clearOpenTimer];
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[14] !== router.events) {
    t9 = () => {
      const handleRouteChangeStart = () => {
        const railRowNavigation = railRowNavigationRef.current;
        railRowNavigationRef.current = false;
        if (railRowNavigation && panelRef.current?.matches(":hover")) {
          armSuppressUntilPointerLeaves();
        }
        clearOpenTimer();
        removePortalTracker();
      };
      router.events.on("routeChangeStart", handleRouteChangeStart);
      return () => {
        router.events.off("routeChangeStart", handleRouteChangeStart);
      };
    };
    t10 = [router.events, removePortalTracker, clearOpenTimer];
    $[14] = router.events;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[17] !== router.events) {
    t11 = () => {
      const settle = () => {
        if (RAIL_HOVER_EXPAND) {
          setInstantClose(true);
          setExpanded(false);
        }
        setOptimisticSection(null);
      };
      router.events.on("routeChangeComplete", settle);
      router.events.on("routeChangeError", settle);
      return () => {
        router.events.off("routeChangeComplete", settle);
        router.events.off("routeChangeError", settle);
      };
    };
    t12 = [router.events];
    $[17] = router.events;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[20] !== instantClose) {
    t13 = () => {
      if (!instantClose) {
        return;
      }
      const id = requestAnimationFrame(() => setInstantClose(false));
      return () => cancelAnimationFrame(id);
    };
    t14 = [instantClose];
    $[20] = instantClose;
    $[21] = t13;
    $[22] = t14;
  } else {
    t13 = $[21];
    t14 = $[22];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[23] !== expanded) {
    t15 = () => {
      pinnedExpanded = expanded;
    };
    t16 = [expanded];
    $[23] = expanded;
    $[24] = t15;
    $[25] = t16;
  } else {
    t15 = $[24];
    t16 = $[25];
  }
  useLayoutEffect(t15, t16);
  let t17;
  let t18;
  if ($[26] !== layoutWidth) {
    t17 = () => {
      document.documentElement.style.setProperty(RAIL_WIDTH_CSS_VAR, `${layoutWidth}px`);
    };
    t18 = [layoutWidth];
    $[26] = layoutWidth;
    $[27] = t17;
    $[28] = t18;
  } else {
    t17 = $[27];
    t18 = $[28];
  }
  useLayoutEffect(t17, t18);
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = [];
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  useLayoutEffect(_temp2, t19);
  let t20;
  let t21;
  if ($[30] !== codesmithEnabled || $[31] !== currentOrg || $[32] !== router) {
    t20 = () => {
      if (!codesmithEnabled) {
        return;
      }
      const handleNewChat = e_1 => {
        if (e_1.altKey && e_1.shiftKey && !e_1.metaKey && !e_1.ctrlKey && e_1.code === "KeyN") {
          e_1.preventDefault();
          const sessionsPath = currentOrg ? `/${currentOrg}/sessions/new` : "/sessions/new";
          router.push(sessionsPath);
        }
      };
      document.addEventListener("keydown", handleNewChat);
      return () => document.removeEventListener("keydown", handleNewChat);
    };
    t21 = [codesmithEnabled, currentOrg, router];
    $[30] = codesmithEnabled;
    $[31] = currentOrg;
    $[32] = router;
    $[33] = t20;
    $[34] = t21;
  } else {
    t20 = $[33];
    t21 = $[34];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[35] !== codesmithAutomationsEnabled || $[36] !== currentOrg || $[37] !== router) {
    t22 = () => {
      if (!codesmithAutomationsEnabled) {
        return;
      }
      const handleAutomations = e_2 => {
        if (e_2.altKey && e_2.shiftKey && !e_2.metaKey && !e_2.ctrlKey && e_2.code === "KeyA") {
          e_2.preventDefault();
          const automationsPath = currentOrg ? `/${currentOrg}/automations` : "/automations";
          router.push(automationsPath);
        }
      };
      document.addEventListener("keydown", handleAutomations);
      return () => document.removeEventListener("keydown", handleAutomations);
    };
    t23 = [codesmithAutomationsEnabled, currentOrg, router];
    $[35] = codesmithAutomationsEnabled;
    $[36] = currentOrg;
    $[37] = router;
    $[38] = t22;
    $[39] = t23;
  } else {
    t22 = $[38];
    t23 = $[39];
  }
  useEffect(t22, t23);
  let t24;
  let t25;
  if ($[40] !== codesmithEnabled || $[41] !== currentOrg || $[42] !== gPrefixActiveRef || $[43] !== router) {
    t24 = () => {
      if (!codesmithEnabled) {
        return;
      }
      const handleCodesmith = e_3 => {
        if (isTypingTarget(e_3.target)) {
          return;
        }
        if (e_3.key === "c" && !e_3.metaKey && !e_3.ctrlKey && !e_3.altKey && !e_3.shiftKey && !gPrefixActiveRef.current) {
          e_3.preventDefault();
          const sessionsPath_0 = currentOrg ? `/${currentOrg}/sessions/new` : "/sessions/new";
          router.push(sessionsPath_0);
        }
      };
      document.addEventListener("keydown", handleCodesmith);
      return () => document.removeEventListener("keydown", handleCodesmith);
    };
    t25 = [codesmithEnabled, currentOrg, gPrefixActiveRef, router];
    $[40] = codesmithEnabled;
    $[41] = currentOrg;
    $[42] = gPrefixActiveRef;
    $[43] = router;
    $[44] = t24;
    $[45] = t25;
  } else {
    t24 = $[44];
    t25 = $[45];
  }
  useEffect(t24, t25);
  const t26 = currentOrg ?? undefined;
  let t27;
  if ($[46] !== globalOrgBlocked || $[47] !== t26) {
    t27 = <SidebarBlockedOrgBanner activeGithubOrgBlocked={globalOrgBlocked} currentOrg={t26} />;
    $[46] = globalOrgBlocked;
    $[47] = t26;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  const t28 = expanded ? "expanded" : "collapsed";
  const t29 = redesign && !expanded && RAIL_REDESIGN_CLASS;
  let t30;
  if ($[49] !== t29) {
    t30 = cn("group relative h-full shrink-0 flex flex-col overflow-hidden", "bg-sidebar shadow-[inset_-1px_0_0_0_var(--color-border)]", t29);
    $[49] = t29;
    $[50] = t30;
  } else {
    t30 = $[50];
  }
  const t31 = instantClose ? "none" : RAIL_TRANSITION_STYLE;
  let t32;
  if ($[51] !== layoutWidth || $[52] !== t31) {
    t32 = {
      width: layoutWidth,
      transition: t31
    };
    $[51] = layoutWidth;
    $[52] = t31;
    $[53] = t32;
  } else {
    t32 = $[53];
  }
  const t33 = expanded ? "Collapse sidebar" : "Expand sidebar";
  let t34;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = () => setExpanded(_temp3);
    $[54] = t34;
  } else {
    t34 = $[54];
  }
  const t35 = expanded ? "Collapse sidebar" : "Expand sidebar";
  const t36 = expanded ? "opacity-0" : "opacity-100";
  const t37 = instantClose ? "transition-none" : "[transition:opacity_100ms_ease-out]";
  let t38;
  if ($[55] !== t36 || $[56] !== t37) {
    t38 = cn("flex size-8 shrink-0 items-center justify-center", t36, t37);
    $[55] = t36;
    $[56] = t37;
    $[57] = t38;
  } else {
    t38 = $[57];
  }
  let t39;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <div className="h-4 w-6 text-[#0c0c0de5] dark:text-blacksmith-yellow"><BlacksmithLogomarkSVG /></div>;
    $[58] = t39;
  } else {
    t39 = $[58];
  }
  let t40;
  if ($[59] !== t38) {
    t40 = <div className={t38}>{t39}</div>;
    $[59] = t38;
    $[60] = t40;
  } else {
    t40 = $[60];
  }
  let t41;
  if ($[61] !== expanded || $[62] !== instantClose) {
    t41 = <div aria-hidden={true} className="pointer-events-none absolute left-[6px] top-1/2 -translate-y-1/2"><BlacksmithWordmarkSVG expanded={expanded} instant={instantClose} className="h-4 w-auto origin-left scale-[1.0485] text-[#0c0c0de5] dark:text-blacksmith-yellow" /></div>;
    $[61] = expanded;
    $[62] = instantClose;
    $[63] = t41;
  } else {
    t41 = $[63];
  }
  let t42;
  if ($[64] !== t35 || $[65] !== t40 || $[66] !== t41) {
    t42 = <button type="button" onClick={t34} aria-label={t35} className="relative flex h-8 w-full items-center hover:opacity-70 transition-opacity cursor-pointer">{t40}{t41}</button>;
    $[64] = t35;
    $[65] = t40;
    $[66] = t41;
    $[67] = t42;
  } else {
    t42 = $[67];
  }
  let t43;
  if ($[68] !== t33 || $[69] !== t42) {
    t43 = <AppHeaderBar><CollapsedIconTooltip label={t33} enabled={true}>{t42}</CollapsedIconTooltip></AppHeaderBar>;
    $[68] = t33;
    $[69] = t42;
    $[70] = t43;
  } else {
    t43 = $[70];
  }
  const t44 = redesign ? undefined : "pt-app-header-gap";
  let t45;
  if ($[71] !== expanded) {
    t45 = <GitHubOrgSelector expanded={expanded} />;
    $[71] = expanded;
    $[72] = t45;
  } else {
    t45 = $[72];
  }
  let t46;
  if ($[73] !== t44 || $[74] !== t45) {
    t46 = <div className={t44}>{t45}</div>;
    $[73] = t44;
    $[74] = t45;
    $[75] = t46;
  } else {
    t46 = $[75];
  }
  const t47 = !redesign && "gap-1 pt-1";
  let t48;
  if ($[76] !== t47) {
    t48 = cn("rail-rows flex flex-col", t47);
    $[76] = t47;
    $[77] = t48;
  } else {
    t48 = $[77];
  }
  const t49 = currentOrg ? true : null;
  const t50 = !!currentOrg;
  let t51;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = handleRailRowClick("actions");
    $[78] = t51;
  } else {
    t51 = $[78];
  }
  const t52 = activeSection === "actions" ? "page" : undefined;
  const t53 = activeSection === "actions" && RAIL_ROW_ACTIVE;
  let t54;
  if ($[79] !== t53) {
    t54 = cn(RAIL_ROW, t53);
    $[79] = t53;
    $[80] = t54;
  } else {
    t54 = $[80];
  }
  let t55;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <RailRowContents icon={<Github className="size-[18px]" />} label="GitHub Actions" />;
    $[81] = t55;
  } else {
    t55 = $[81];
  }
  let t56;
  if ($[82] !== runsJobsHref || $[83] !== t49 || $[84] !== t50 || $[85] !== t52 || $[86] !== t54) {
    t56 = <RoutePrefetchLink href={runsJobsHref} prefetch={t49} prefetchOnIntent={t50} onClick={t51} aria-current={t52} className={t54}>{t55}</RoutePrefetchLink>;
    $[82] = runsJobsHref;
    $[83] = t49;
    $[84] = t50;
    $[85] = t52;
    $[86] = t54;
    $[87] = t56;
  } else {
    t56 = $[87];
  }
  let t57;
  if ($[88] !== slim || $[89] !== t56) {
    t57 = <CollapsedIconTooltip label="GitHub Actions" enabled={slim}>{t56}</CollapsedIconTooltip>;
    $[88] = slim;
    $[89] = t56;
    $[90] = t57;
  } else {
    t57 = $[90];
  }
  let t58;
  if ($[91] !== activeSection || $[92] !== codesmithEnabled || $[93] !== codesmithRowHovered || $[94] !== currentOrg || $[95] !== sessionsNewHref || $[96] !== slim) {
    t58 = codesmithEnabled && (() => {
      const codesmithRow = <RoutePrefetchLink href={sessionsNewHref} prefetchOnIntent={!!currentOrg} onMouseEnter={() => setCodesmithRowHovered(true)} onMouseLeave={() => setCodesmithRowHovered(false)} onClick={handleRailRowClick("codesmith")} aria-current={activeSection === "codesmith" ? "page" : undefined} className={cn(RAIL_ROW, activeSection === "codesmith" && RAIL_ROW_ACTIVE)}><RailRowContents icon={<CodesmithLottieIcon play={codesmithRowHovered} />} label="[code]smith" /></RoutePrefetchLink>;
      return <Tooltip><TooltipTrigger asChild={true}>{codesmithRow}</TooltipTrigger>{slim && <TooltipContent side="right" className="flex items-center gap-2"><span className="leading-none">[code]smith</span><ShortcutKeys keys={["C"]} /></TooltipContent>}</Tooltip>;
    })();
    $[91] = activeSection;
    $[92] = codesmithEnabled;
    $[93] = codesmithRowHovered;
    $[94] = currentOrg;
    $[95] = sessionsNewHref;
    $[96] = slim;
    $[97] = t58;
  } else {
    t58 = $[97];
  }
  let t59;
  if ($[98] !== t48 || $[99] !== t57 || $[100] !== t58) {
    t59 = <nav className={t48} aria-label="Apps">{t57}{t58}</nav>;
    $[98] = t48;
    $[99] = t57;
    $[100] = t58;
    $[101] = t59;
  } else {
    t59 = $[101];
  }
  let t60;
  if ($[102] !== activeSection || $[103] !== expanded || $[104] !== redesign || $[105] !== slim) {
    t60 = !redesign && <SidebarAdminRow expanded={expanded} active={activeSection === "admin"} onClick={handleRailRowClick("admin")} hideLabelWhenCollapsed={slim} />;
    $[102] = activeSection;
    $[103] = expanded;
    $[104] = redesign;
    $[105] = slim;
    $[106] = t60;
  } else {
    t60 = $[106];
  }
  let t61;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <div className="flex-1" />;
    $[107] = t61;
  } else {
    t61 = $[107];
  }
  let t62;
  if ($[108] !== activeSection || $[109] !== expanded || $[110] !== redesign || $[111] !== slim) {
    t62 = redesign ? <SidebarAdminRow expanded={expanded} active={activeSection === "admin"} onClick={handleRailRowClick("admin")} hideLabelWhenCollapsed={slim} divider={false} /> : null;
    $[108] = activeSection;
    $[109] = expanded;
    $[110] = redesign;
    $[111] = slim;
    $[112] = t62;
  } else {
    t62 = $[112];
  }
  const t63 = currentOrg ?? undefined;
  const t64 = activeApp === "codesmith";
  let t65;
  if ($[113] !== currentOrg || $[114] !== t64) {
    t65 = settingsPagePath(currentOrg, t64);
    $[113] = currentOrg;
    $[114] = t64;
    $[115] = t65;
  } else {
    t65 = $[115];
  }
  let t66;
  if ($[116] !== activeSection || $[117] !== expanded || $[118] !== redesign || $[119] !== slim || $[120] !== t62 || $[121] !== t63 || $[122] !== t65) {
    t66 = <SidebarGlobalFooter leading={t62} expanded={expanded} currentOrg={t63} settingsHref={t65} activeSection={activeSection} onSectionClick={handleRailRowClick} className="pb-2" hideLabelWhenCollapsed={slim} statusAsHoverCard={slim} redesign={redesign} />;
    $[116] = activeSection;
    $[117] = expanded;
    $[118] = redesign;
    $[119] = slim;
    $[120] = t62;
    $[121] = t63;
    $[122] = t65;
    $[123] = t66;
  } else {
    t66 = $[123];
  }
  let t67;
  if ($[124] !== t43 || $[125] !== t46 || $[126] !== t59 || $[127] !== t60 || $[128] !== t66) {
    t67 = <div className="flex h-full w-full flex-col px-2">{t43}{t46}{t59}{t60}{t61}{t66}</div>;
    $[124] = t43;
    $[125] = t46;
    $[126] = t59;
    $[127] = t60;
    $[128] = t66;
    $[129] = t67;
  } else {
    t67 = $[129];
  }
  let t68;
  if ($[130] !== handleMouseEnter || $[131] !== t28 || $[132] !== t30 || $[133] !== t32 || $[134] !== t67) {
    t68 = <div ref={panelRef} data-slot="app-switcher-rail" data-state={t28} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={t30} style={t32}>{t67}</div>;
    $[130] = handleMouseEnter;
    $[131] = t28;
    $[132] = t30;
    $[133] = t32;
    $[134] = t67;
    $[135] = t68;
  } else {
    t68 = $[135];
  }
  let t69;
  if ($[136] !== t27 || $[137] !== t68) {
    t69 = <>{t27}{t68}</>;
    $[136] = t27;
    $[137] = t68;
    $[138] = t69;
  } else {
    t69 = $[138];
  }
  return t69;
}
function _temp3(prev) {
  return !prev;
}
function _temp2() {
  return _temp;
}
function _temp() {
  document.documentElement.style.removeProperty(RAIL_WIDTH_CSS_VAR);
}